import { Component, effect, ElementRef, inject, input, NgZone, OnDestroy, viewChild } from '@angular/core';
import { ChartConfiguration, ChartType, DefaultDataPoint } from 'chart.js';
import Chart from 'chart.js/auto';

@Component({
  selector: 'sb-chart',
  templateUrl: './chart.component.html',
  standalone: true,
  host: {
    style: 'display: block; position: relative;',
    '[style.height]': 'height()',
  },
})
export class ChartComponent<TType extends ChartType = ChartType, TData = DefaultDataPoint<TType>, TLabel = unknown>
  implements OnDestroy
{
  private readonly zone = inject(NgZone);

  public ariaLabel = input<string>('');
  public ariaLabelledBy = input<string>('');

  public type = input.required<ChartConfiguration<TType, TData, TLabel>['type']>();
  public data = input.required<ChartConfiguration<TType, TData, TLabel>['data']>();
  public options = input.required<ChartConfiguration<TType, TData, TLabel>['options']>();

  public height = input.required<string>();

  private chart?: Chart<TType, TData, TLabel>;
  private canvas = viewChild('chart', {
    read: ElementRef<HTMLCanvasElement>,
  });

  constructor() {
    effect(() => {
      const canvas = this.canvas();
      const data = this.data();

      if (!data || !canvas) {
        return;
      }

      if (this.chart) {
        this.update();
        return;
      }

      this.render();
    });
  }

  private render() {
    this.zone.runOutsideAngular(() => {
      const canvas = this.canvas();
      if (!canvas) {
        return;
      }
      this.chart = new Chart(canvas.nativeElement, this.getChartConfiguration());
    });
  }

  private getChartConfiguration(): ChartConfiguration<TType, TData, TLabel> {
    return {
      type: this.type(),
      data: this.data(),
      options: this.options(),
    };
  }

  private update() {
    this.zone.runOutsideAngular(() => {
      if (!this.chart) {
        return;
      }

      this.chart.data = this.data();
      this.chart.update();
    });
  }

  public ngOnDestroy(): void {
    this.destroy();
  }

  public destroy() {
    if (!this.chart) {
      return;
    }
    this.chart.destroy();
    this.chart = void 0;
  }
}
